import { useEffect, useState } from 'react';
import {
  ESTADOS_DOSSIE,
  getDossie,
  getLoopErro,
  setDossieEstado,
  type Dossie,
  type MetricasDossie,
} from '../lib/api';

// Shape vindo de admin-api/routers/loop_erro.py (GET /loop-erro).
// Cada seção pode voltar como [{ erro: '...' }] quando a tabela de origem
// ainda não existe — por isso o campo `erro` opcional em todas elas.
interface LoopIssue {
  id?: number;
  assinatura?: string;
  tipo?: string | null;
  fase?: string | null;
  n_casos?: number | null;
  n_orgs?: number | null;
  issue_status?: string | null;
  created_at?: string | null;
  fix_id?: number | null;
  classe?: string | null;
  risco?: string | null;
  flag_proposto?: string | null;
  fix_status?: string | null;
  golden5?: any;
  fix_em?: string | null;
  raiz?: string | null;
  fix_abordagem?: string | null;
  n_solucoes?: number | null;
  erro?: string;
}
interface FunilEtapa {
  etapa?: string | null;
  n?: number | null;
  erro?: string;
}
interface ErroClasse {
  classe?: string | null;
  d7?: number | null;
  d28?: number | null;
  erro?: string;
}
interface AvisoCliente {
  casos?: number | null;
  avisados?: number | null;
  orgs?: number | null;
  orgs_que_voltaram?: number | null;
  erro?: string;
}

// ── Dossiês do modelo híbrido (erro_dossie + monitoring.dossie_erro) ───────

// Uma cor por estado do contrato. Quem olha a aba precisa ver, de longe,
// quantos dossiês estão parados em `novo` (ninguém abriu) ou `pronto_flip`
// (esperando o GO).
const CORES_ESTADO: Record<string, string> = {
  novo: 'bg-indigo-600/20 text-indigo-300',
  em_sessao: 'bg-amber-600/20 text-amber-300',
  nao_e_nosso: 'bg-slate-700/40 text-slate-400',
  fix_gated: 'bg-sky-600/20 text-sky-300',
  pronto_flip: 'bg-emerald-600/20 text-emerald-300',
  flipado: 'bg-emerald-500/25 text-emerald-200',
  remediado: 'bg-teal-600/20 text-teal-300',
  descartado: 'bg-slate-800/60 text-slate-500',
};

function EstadoDossieTag({ estado }: { estado?: string | null }) {
  const e = estado ?? 'novo';
  const cls = CORES_ESTADO[e] ?? 'bg-slate-700/40 text-slate-300';
  return <span className={`rounded px-2 py-0.5 text-xs font-semibold ${cls}`}>{e}</span>;
}

/** Idade entre dois carimbos ISO, em horas. '—' quando falta um dos lados. */
function horas(a?: string | null, b?: string | null): string {
  if (!a || !b) return '—';
  const ta = Date.parse(a.endsWith('Z') || /[+-]\d\d:?\d\d$/.test(a) ? a : `${a}Z`);
  const tb = Date.parse(b.endsWith('Z') || /[+-]\d\d:?\d\d$/.test(b) ? b : `${b}Z`);
  if (Number.isNaN(ta) || Number.isNaN(tb)) return '—';
  return `${((tb - ta) / 3_600_000).toFixed(1)}h`;
}

function lista(v?: Array<number | string> | null): string {
  if (!v || v.length === 0) return '—';
  return `${v.length} (${v.slice(0, 5).join(', ')}${v.length > 5 ? '…' : ''})`;
}

// Rótulos das etapas de monitoring.dossie_erro.metricas().medias_horas.
const ROTULO_ETAPA: Record<string, string> = {
  h_erro_dossie: 'erro → dossiê (h)',
  h_dossie_sessao: 'dossiê → sessão (h)',
  h_sessao_fix: 'sessão → fix (h)',
  h_fix_flip: 'fix → flip (h)',
  h_flip_remediado: 'flip → remediado (h)',
};

/** metricas() vem de um módulo fora do admin-api: aceito vários nomes de chave
 *  em vez de a aba ficar muda se ele renomear algo. Hoje é `medias_horas`. */
function medianasPorEtapa(m: MetricasDossie): Array<[string, unknown]> {
  const pd = ((m as any).medias_horas ??
    m.por_dossie ??
    (m as any).medianas ??
    (m as any).por_etapa) as unknown;
  if (pd && typeof pd === 'object' && !Array.isArray(pd)) return Object.entries(pd as object);
  return Object.entries(m).filter(
    ([k, v]) => /^h_|_horas$/i.test(k) && (typeof v === 'number' || v === null),
  );
}

function recorrenciaPorClasse(m: MetricasDossie): Array<Record<string, unknown>> {
  const pc = (m.por_classe ?? (m as any).recorrencia ?? (m as any).por_classe_30d) as unknown;
  if (Array.isArray(pc)) return pc as Array<Record<string, unknown>>;
  if (pc && typeof pc === 'object') {
    return Object.entries(pc as object).map(([classe, v]) =>
      v && typeof v === 'object' && !Array.isArray(v)
        ? { classe, ...(v as Record<string, unknown>) }
        : { classe, valor: v as unknown },
    );
  }
  return [];
}

function celula(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(1);
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

const RUINS = new Set([
  'par_nao_provou',
  'golden5_vermelho',
  'autor_falhou',
  'patch_nao_aplica',
  'patch_syntax_erro',
  'golden5_erro',
]);

function EstadoTag({ status }: { status?: string | null }) {
  const st = status ?? 'sem_proposta';
  let cls = 'bg-amber-600/20 text-amber-300';
  let label = st;
  if (st === 'pronto_flip') {
    cls = 'bg-emerald-600/20 text-emerald-300';
    label = 'pronto p/ GO';
  } else if (st === 'shipped') {
    cls = 'bg-emerald-600/20 text-emerald-300';
    label = 'no ar';
  } else if (RUINS.has(st)) {
    cls = 'bg-rose-600/20 text-rose-300';
  } else if (st === 'sem_proposta') {
    cls = 'bg-slate-700/40 text-slate-400';
    label = 'sem proposta';
  }
  return <span className={`rounded px-2 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>;
}

// prova = golden5 (vizinho idêntico) + par (casos quebrados que passaram a liquidar em clone)
function provaTexto(golden5: any): string {
  if (!golden5 || typeof golden5 !== 'object' || Object.keys(golden5).length === 0) return '—';
  let txt = golden5.verde ? '🟢 vizinho' : '🔴 vizinho';
  const par = golden5.par;
  if (par && typeof par === 'object') {
    txt += par.par_ok ? ` · 🟢 par ${par.liquidaram}/${par.testados}` : ' · 🔴 par';
  }
  return txt;
}

function Erro({ linhas }: { linhas: Array<{ erro?: string }> }) {
  const e = linhas.find((l) => l.erro);
  if (!e) return null;
  return <div className="mb-2 text-xs text-rose-300/80">Consulta indisponível: {e.erro}</div>;
}

export default function LoopErroPage() {
  const [issues, setIssues] = useState<LoopIssue[]>([]);
  const [funil, setFunil] = useState<FunilEtapa[]>([]);
  const [errosPorClasse, setErrosPorClasse] = useState<ErroClasse[]>([]);
  const [aviso, setAviso] = useState<AvisoCliente[]>([]);
  const [dossies, setDossies] = useState<Dossie[]>([]);
  const [metricas, setMetricas] = useState<MetricasDossie>({});
  const [avisoBackend, setAvisoBackend] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  // modal do markdown + estado do seletor (por dossiê)
  const [md, setMd] = useState<{ id: number; titulo: string; texto: string } | null>(null);
  const [mdLoading, setMdLoading] = useState<number | null>(null);
  const [salvando, setSalvando] = useState<number | null>(null);
  const [acaoErro, setAcaoErro] = useState<string | null>(null);

  function carregar() {
    return getLoopErro()
      .then((r) => {
        setIssues((r.data.issues ?? []) as LoopIssue[]);
        setFunil((r.data.funil ?? []) as FunilEtapa[]);
        setErrosPorClasse((r.data.erros_por_classe ?? []) as ErroClasse[]);
        setAviso((r.data.aviso_ao_cliente ?? []) as AvisoCliente[]);
        setDossies(r.data.dossies ?? []);
        setMetricas(r.data.metricas_dossie ?? {});
        setAvisoBackend(r.data.aviso ?? null);
      })
      .catch((e) => setErr(e?.message ?? 'erro'));
  }

  useEffect(() => {
    carregar().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function abrirDossie(d: Dossie) {
    setMdLoading(d.id);
    setAcaoErro(null);
    try {
      const r = await getDossie(d.id);
      if (r.data.ok) {
        setMd({
          id: d.id,
          titulo: r.data.path ?? d.assinatura ?? `dossiê ${d.id}`,
          texto: r.data.markdown ?? '',
        });
      } else {
        setAcaoErro(r.data.erro ?? 'dossiê indisponível');
      }
    } catch (e: any) {
      setAcaoErro(e?.response?.data?.detail ?? e?.message ?? 'erro ao ler o dossiê');
    } finally {
      setMdLoading(null);
    }
  }

  // Grava o estado e recarrega: o timestamp da etapa é carimbado no backend,
  // então as colunas de idade só ficam certas depois do refetch.
  async function mudarEstado(d: Dossie, estado: string) {
    if (!estado || estado === d.estado) return;
    setSalvando(d.id);
    setAcaoErro(null);
    try {
      const r = await setDossieEstado(d.id, estado);
      if (!r.data.ok) setAcaoErro(r.data.erro ?? 'UPDATE não aplicou');
      await carregar();
    } catch (e: any) {
      setAcaoErro(e?.response?.data?.detail ?? e?.message ?? 'erro ao gravar o estado');
    } finally {
      setSalvando(null);
    }
  }

  if (loading) return <div className="p-6 text-slate-400">Carregando…</div>;
  if (err) return <div className="p-6 text-rose-400">Erro: {err}</div>;

  const dossiesOk = dossies.filter((d) => !d.erro);
  const medianas = medianasPorEtapa(metricas);
  const classes = recorrenciaPorClasse(metricas);
  const colsClasse = Array.from(new Set(classes.flatMap((c) => Object.keys(c))));
  const porEstado = (metricas.por_estado ?? {}) as Record<string, number>;
  const nDossiesMet = (metricas.n_dossies ?? null) as number | null;
  const issuesOk = issues.filter((i) => !i.erro);
  const funilOk = funil.filter((f) => !f.erro);
  const errosOk = errosPorClasse.filter((e) => !e.erro);
  const av = aviso.find((a) => !a.erro) ?? {};
  const temAviso = av.casos !== undefined && av.casos !== null;
  const mudos = (av.casos ?? 0) - (av.avisados ?? 0);
  const naoVoltaram = (av.orgs ?? 0) - (av.orgs_que_voltaram ?? 0);

  return (
    <div className="space-y-8 p-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-100">Loop de erro</h1>
        <p className="mt-1 text-sm text-slate-400">
          Cadeia monitorada: <code>erro_intake</code> 06:30 → <code>system_fix_loop</code> 06:45 →{' '}
          <code>fix_pipeline</code> 07:15 → <b className="text-emerald-300">pronto_flip</b> → GO no
          Telegram (<code>flip &lt;id&gt;</code>). Só o estado <b>pronto_flip</b> aceita o GO.
        </p>
      </div>

      {avisoBackend && (
        <div className="rounded-lg border border-amber-700/40 bg-amber-900/10 px-3 py-2 text-xs text-amber-300">
          ⚠️ {avisoBackend}
        </div>
      )}
      {acaoErro && (
        <div className="rounded-lg border border-rose-700/40 bg-rose-900/10 px-3 py-2 text-xs text-rose-300">
          {acaoErro}
        </div>
      )}

      {/* Dossiês do modelo híbrido (erro_dossie), 30 dias */}
      <section>
        <h2 className="mb-2 text-sm font-medium text-slate-300">
          🗂️ Dossiês (híbrido) — {dossiesOk.length} nos últimos 30 dias
        </h2>
        <p className="mb-3 text-xs text-slate-500">
          Um dossiê por erro de cliente em <code>/opt/calcfin/dossies/*.md</code>. Fluxo:{' '}
          <code>novo</code> → <code>em_sessao</code> →{' '}
          <code>nao_e_nosso</code> | <code>fix_gated</code> → <code>pronto_flip</code> →{' '}
          <code>flipado</code> → <code>remediado</code>. Trocar o estado carimba o
          timestamp da etapa no backend (o 1º carimbo é preservado).
        </p>
        <div className="overflow-x-auto rounded-xl border border-slate-800">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900/60 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left">Estado</th>
                <th className="px-3 py-2 text-left">Assinatura</th>
                <th className="px-3 py-2 text-left">Caso principal</th>
                <th className="px-3 py-2 text-left">Orgs</th>
                <th className="px-3 py-2 text-center">erro→dossiê</th>
                <th className="px-3 py-2 text-center">dossiê→sessão</th>
                <th className="px-3 py-2 text-left">Flag / lane</th>
                <th className="px-3 py-2 text-left">Dossiê</th>
              </tr>
            </thead>
            <tbody>
              {dossiesOk.map((d) => (
                <tr key={d.id} className="border-t border-slate-800">
                  <td className="whitespace-nowrap px-3 py-2">
                    <EstadoDossieTag estado={d.estado} />
                    <select
                      className="mt-1 block w-full rounded border border-slate-700 bg-slate-900 px-1 py-0.5 text-xs text-slate-200 disabled:opacity-50"
                      value={d.estado ?? 'novo'}
                      disabled={salvando === d.id}
                      onChange={(ev) => mudarEstado(d, ev.target.value)}
                    >
                      {ESTADOS_DOSSIE.map((e) => (
                        <option key={e} value={e}>
                          {e}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <code className="text-xs text-slate-200">{d.assinatura ?? '—'}</code>
                    <div className="mt-0.5 text-xs text-slate-500">
                      issue {d.issue_id ?? '—'} · dossiê {d.id}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="font-semibold text-slate-200">
                      {d.processo_principal ?? '—'}
                    </span>
                    <div className="mt-0.5 text-xs text-slate-500">{lista(d.processo_ids)}</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-400">{lista(d.org_ids)}</td>
                  <td className="px-3 py-2 text-center text-slate-300">
                    {horas(d.erro_at, d.dossie_at)}
                  </td>
                  <td className="px-3 py-2 text-center text-slate-300">
                    {horas(d.dossie_at, d.sessao_at)}
                  </td>
                  <td className="px-3 py-2">
                    <code className="text-xs text-slate-300">{d.flag ?? '—'}</code>
                    {d.lane && <div className="mt-0.5 text-xs text-slate-500">{d.lane}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="rounded border border-slate-700 px-2 py-0.5 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                      disabled={!d.path || mdLoading === d.id}
                      onClick={() => abrirDossie(d)}
                    >
                      {mdLoading === d.id ? '…' : 'ver dossiê'}
                    </button>
                  </td>
                </tr>
              ))}
              {dossiesOk.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-4 text-center text-slate-500">
                    Nenhum dossiê nos últimos 30 dias.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Métricas do loop híbrido (monitoring.dossie_erro.metricas) */}
      <section>
        <h2 className="mb-2 text-sm font-medium text-slate-300">⏱️ Métricas 30d</h2>
        <p className="mb-3 text-xs text-slate-500">
          Médias por etapa (horas) e recorrência por classe depois do flip — de{' '}
          <code>monitoring.dossie_erro.metricas()</code>.{' '}
          <b>recorrencia_pos_flip</b> = ERROR de cliente com a MESMA assinatura depois
          do flip; <code>—</code> quando a classe ainda não foi flipada.
        </p>
        {medianas.length === 0 && classes.length === 0 ? (
          <div className="rounded-xl border border-slate-800 px-3 py-4 text-center text-slate-500">
            Sem métricas (módulo indisponível ou nenhum dossiê fechado).
          </div>
        ) : (
          <div className="space-y-4">
            {medianas.length > 0 && (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {medianas.map(([k, v]) => (
                  <div key={k} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                    <div className="text-2xl font-semibold text-slate-100">{celula(v)}</div>
                    <div className="text-xs text-slate-500">
                      {ROTULO_ETAPA[k] ?? k.replace(/_/g, ' ')}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {Object.keys(porEstado).length > 0 && (
              <div className="flex flex-wrap gap-2 text-xs">
                {nDossiesMet !== null && (
                  <span className="rounded bg-slate-800/60 px-2 py-1 text-slate-300">
                    {nDossiesMet} dossiê(s) na janela
                  </span>
                )}
                {Object.entries(porEstado).map(([e, n]) => (
                  <span key={e} className="flex items-center gap-1 rounded bg-slate-900/60 px-2 py-1">
                    <EstadoDossieTag estado={e} />
                    <span className="font-semibold text-slate-200">{n}</span>
                  </span>
                ))}
              </div>
            )}
            {classes.length > 0 && (
              <div className="overflow-x-auto rounded-xl border border-slate-800">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-900/60 text-xs uppercase text-slate-400">
                    <tr>
                      {colsClasse.map((c) => (
                        <th key={c} className="px-3 py-2 text-left">
                          {c.replace(/_/g, ' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {classes.map((row, i) => (
                      <tr key={i} className="border-t border-slate-800">
                        {colsClasse.map((c) => (
                          <td key={c} className="px-3 py-2 text-slate-300">
                            {celula(row[c])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Clientes avisados × orgs que voltaram (28d) */}
      <section>
        <h2 className="mb-2 text-sm font-medium text-slate-300">
          📣 Aviso ao cliente e retorno (28 dias)
        </h2>
        <Erro linhas={aviso} />
        {temAviso ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
              <div className="text-2xl font-semibold text-slate-100">{av.casos ?? 0}</div>
              <div className="text-xs text-slate-500">erros de culpa nossa</div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
              <div className="text-2xl font-semibold text-slate-100">{av.avisados ?? 0}</div>
              <div className="text-xs text-slate-500">clientes avisados (falha_causa)</div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
              <div className="text-2xl font-semibold text-rose-300">{mudos}</div>
              <div className="text-xs text-slate-500">falharam em silêncio</div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
              <div className="text-2xl font-semibold text-amber-300">
                {naoVoltaram}/{av.orgs ?? 0}
              </div>
              <div className="text-xs text-slate-500">orgs que não voltaram</div>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-800 px-3 py-4 text-center text-slate-500">
            Sem dados.
          </div>
        )}
      </section>

      {/* Estado por issue */}
      <section>
        <h2 className="mb-2 text-sm font-medium text-slate-300">
          🛠️ Issues de erro e o estado do fix — {issuesOk.length}
        </h2>
        <p className="mb-3 text-xs text-slate-500">
          prova = golden5 (vizinho idêntico) + par (casos quebrados que passaram a liquidar em
          clone). A coluna GO mostra o comando do Telegram quando o fix está em{' '}
          <code>pronto_flip</code>.
        </p>
        <Erro linhas={issues} />
        <div className="overflow-x-auto rounded-xl border border-slate-800">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900/60 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left">Assinatura / causa-raiz</th>
                <th className="px-3 py-2 text-center">Casos</th>
                <th className="px-3 py-2 text-center">Orgs</th>
                <th className="px-3 py-2 text-center">Estado</th>
                <th className="px-3 py-2 text-left">Flag</th>
                <th className="px-3 py-2 text-center">Classe/risco</th>
                <th className="px-3 py-2 text-center">Soluç.</th>
                <th className="px-3 py-2 text-left">Prova</th>
                <th className="px-3 py-2 text-left">GO</th>
              </tr>
            </thead>
            <tbody>
              {issuesOk.map((it, idx) => (
                <tr key={it.id ?? `${it.assinatura}-${idx}`} className="border-t border-slate-800">
                  <td className="px-3 py-2">
                    <code className="text-xs text-slate-200">{it.assinatura}</code>
                    {it.raiz && (
                      <div className="mt-0.5 text-xs text-slate-500">{it.raiz.slice(0, 150)}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center font-semibold text-slate-200">
                    {it.n_casos ?? 0}
                  </td>
                  <td className="px-3 py-2 text-center text-slate-300">{it.n_orgs ?? 0}</td>
                  <td className="px-3 py-2 text-center">
                    <EstadoTag status={it.fix_status} />
                  </td>
                  <td className="px-3 py-2">
                    <code className="text-xs text-slate-300">{it.flag_proposto ?? '—'}</code>
                  </td>
                  <td className="px-3 py-2 text-center text-xs text-slate-400">
                    {it.classe ?? '—'}/{it.risco ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-center text-slate-300">{it.n_solucoes ?? 0}</td>
                  <td className="px-3 py-2 text-xs text-slate-300">{provaTexto(it.golden5)}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {it.fix_status === 'pronto_flip' ? (
                      <code className="text-emerald-300">flip {it.fix_id}</code>
                    ) : (
                      ''
                    )}
                  </td>
                </tr>
              ))}
              {issuesOk.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-4 text-center text-slate-500">
                    Nenhuma issue de erro.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Funil por etapa */}
      <section>
        <h2 className="mb-2 text-sm font-medium text-slate-300">⛓️ Funil do loop por etapa</h2>
        <p className="mb-3 text-xs text-slate-500">Onde cada proposta parou.</p>
        <Erro linhas={funil} />
        <div className="overflow-x-auto rounded-xl border border-slate-800">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900/60 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left">Etapa</th>
                <th className="px-3 py-2 text-center">n</th>
              </tr>
            </thead>
            <tbody>
              {funilOk.map((f, i) => (
                <tr key={`${f.etapa}-${i}`} className="border-t border-slate-800">
                  <td className="px-3 py-2">
                    <EstadoTag status={f.etapa} />
                  </td>
                  <td className="px-3 py-2 text-center font-semibold text-slate-200">{f.n ?? 0}</td>
                </tr>
              ))}
              {funilOk.length === 0 && (
                <tr>
                  <td colSpan={2} className="px-3 py-4 text-center text-slate-500">
                    Sem etapas.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ERROR de cliente por classe 7d x 28d */}
      <section>
        <h2 className="mb-2 text-sm font-medium text-slate-300">
          🔴 ERROR de cliente por classe (7d × 28d)
        </h2>
        <p className="mb-3 text-xs text-slate-500">
          O que o loop existe para reduzir. Exclui a org 3 (testes internos).
        </p>
        <Erro linhas={errosPorClasse} />
        <div className="overflow-x-auto rounded-xl border border-slate-800">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900/60 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left">Classe</th>
                <th className="px-3 py-2 text-center">7d</th>
                <th className="px-3 py-2 text-center">28d</th>
              </tr>
            </thead>
            <tbody>
              {errosOk.map((e, i) => (
                <tr key={`${e.classe}-${i}`} className="border-t border-slate-800">
                  <td className="px-3 py-2 text-slate-200">{e.classe}</td>
                  <td className="px-3 py-2 text-center font-semibold text-slate-200">{e.d7 ?? 0}</td>
                  <td className="px-3 py-2 text-center text-slate-300">{e.d28 ?? 0}</td>
                </tr>
              ))}
              {errosOk.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-3 py-4 text-center text-slate-500">
                    Sem erros na janela.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Modal do markdown do dossiê (só leitura) */}
      {md && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-6"
          onClick={() => setMd(null)}
        >
          <div
            className="w-full max-w-4xl rounded-xl border border-slate-700 bg-slate-950 p-5"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="mb-3 flex items-start justify-between gap-4">
              <code className="break-all text-xs text-slate-400">{md.titulo}</code>
              <button
                type="button"
                className="rounded border border-slate-700 px-2 py-0.5 text-xs text-slate-300 hover:bg-slate-800"
                onClick={() => setMd(null)}
              >
                fechar
              </button>
            </div>
            <pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap break-words text-xs leading-relaxed text-slate-200">
              {md.texto || '(vazio)'}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
